import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HomeTaskConsoleTable } from "@/components/agentic-surface/widgets/home/HomeTaskConsoleTable";
import {
  buildNeedsYouRows,
  defaultSortDirection,
  filterNeedsYouTasks,
  sortNeedsYouRowsByColumn,
  type NeedsYouSortColumn,
  type NeedsYouSortDirection,
} from "@/components/agentic-surface/widgets/home/needsYouTableModel";
import { taskHasConfiguredReview } from "@/components/review/isReviewDeckArtefact";
import { useOpenTaskReview } from "@/components/review/useOpenTaskReview";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { TaskActionItem, TaskSurfaceCard } from "@/services/api/agentic-v2/types";

export type TaskReviewTableProps = {
  tasks: TaskActionItem[];
  loading: boolean;
  emptyMessage: string;
  testId: string;
  /** Opens tasks the review workspace can't act on (e.g. deck-only) in the host's task thread pane. */
  onOpenThread?: (taskId: string) => void;
};

/** Searchable, sortable task table for a lead or client — Review opens the task in the review workspace. */
export function TaskReviewTable({ tasks, loading, emptyMessage, testId, onOpenThread }: TaskReviewTableProps) {
  const openTaskReview = useOpenTaskReview();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<NeedsYouSortColumn>("status");
  const [sortDirection, setSortDirection] = useState<NeedsYouSortDirection>("desc");

  const visibleTasks = useMemo(() => {
    const rows = buildNeedsYouRows(filterNeedsYouTasks(tasks, searchQuery, "all"));
    return sortNeedsYouRowsByColumn(rows, sortColumn, sortDirection).map((row) => row.task);
  }, [tasks, searchQuery, sortColumn, sortDirection]);

  const handleSortColumnChange = useCallback(
    (column: NeedsYouSortColumn) => {
      if (column === sortColumn) {
        setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
        return;
      }
      setSortColumn(column);
      setSortDirection(defaultSortDirection(column));
    },
    [sortColumn],
  );

  const handleReviewTask = useCallback(
    async (task: TaskSurfaceCard) => {
      if (onOpenThread) {
        const detail = await queryClient.fetchQuery({
          queryKey: ["task-review-workspace", task._id],
          queryFn: () => AgenticV2API.getTask(task._id),
        });
        if (!taskHasConfiguredReview(detail)) {
          onOpenThread(task._id);
          return;
        }
      }
      openTaskReview(task._id, task.name, { focus: "case" });
    },
    [onOpenThread, openTaskReview, queryClient],
  );

  return (
    <HomeTaskConsoleTable
      tasks={visibleTasks}
      loading={loading}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSortColumnChange={handleSortColumnChange}
      onReviewTask={handleReviewTask}
      firstColumn="status"
      emptyMessage={emptyMessage}
      searchPlaceholder="Search subtasks, runs…"
      testId={testId}
    />
  );
}
